/**
 * Anonymous browser ID for cart recovery.
 *
 * Generated client-side on first visit, persisted in two places for
 * resilience:
 *   1. A cookie (`tct_anon_id`, 1-year expiry, SameSite=Lax) — survives
 *      hard refreshes and most browser sessions; readable by the same
 *      origin only.
 *   2. localStorage — fallback for browsers where third-party-cookie
 *      blocking strips first-party cookies (Safari ITP edge cases).
 *
 * The ID is a UUIDv4-ish 32-char hex string. Stable across visits — never
 * regenerated unless both stores are cleared (cookie + localStorage).
 *
 * This is NOT a user identifier — no PII attached. It's purely a key to
 * look up "did this browser have a cart in progress?" in MongoDB.
 */

const KEY = "tct_anon_id";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value) {
  if (typeof document === "undefined") return;
  const isHttps = window.location?.protocol === "https:";
  // SameSite=Lax + Secure (on HTTPS) + 1-year max-age. We intentionally
  // omit HttpOnly since the cookie is read client-side; the value is not
  // a secret, just a routing key.
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    "path=/",
    `max-age=${ONE_YEAR_SECONDS}`,
    "SameSite=Lax",
  ];
  if (isHttps) parts.push("Secure");
  document.cookie = parts.join("; ");
}

function generateAnonId() {
  // Browser-native crypto.randomUUID() (Chrome 92+, Safari 15.4+, FF 95+) —
  // falls back to Math.random for legacy browsers (still gives 32 hex chars).
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, "");
    }
  } catch { /* ignore */ }
  // Fallback: 32 hex chars from Math.random. Less entropy but adequate
  // for cart-recovery key purposes (collision risk is irrelevant here).
  return Array.from({ length: 4 }, () =>
    Math.random().toString(16).slice(2, 10).padStart(8, "0")
  ).join("");
}

/**
 * Get-or-create the persistent anonymous browser ID.
 *
 * Idempotent — calling this multiple times returns the same value within
 * a session. Side effect: writes the cookie + localStorage on first call.
 *
 * @returns {string} 32-char hex anon_id
 */
export function getAnonId() {
  let id = readCookie(KEY);
  if (!id) {
    try { id = localStorage.getItem(KEY); } catch { /* ignore */ }
  }
  if (!id || id.length < 8) {
    id = generateAnonId();
  }
  // Re-write to both stores on every read — refreshes the cookie max-age
  // and heals the case where one store was cleared (private browsing
  // session ended, etc.) but the other survived.
  writeCookie(KEY, id);
  try { localStorage.setItem(KEY, id); } catch { /* ignore */ }
  return id;
}
